import { useMatch, useParams } from "react-router-dom";
import { BASE_DIRECTORIES, resolveRouterPath } from "files/hooks/useFiles";
import { useProjectFilesDirectories } from "files/hooks/useProjectFilesContext";
import BaseDirectory from "files/types/enums/baseDirectory";
import { SWRResponse } from "swr";
import useProjectsAccessList, { ProjectRolePrivilege } from "~/hooks/useProjectsAccessList";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";
import { ProjectListItem } from "~/services/projects";

/**
 * Helper function to determine the proper display for a file based on key and/or whether
 * it's still loading (in the case of project titles which are resolved from an async response)
 * @param key - s3 object key
 * @param multiProject - Impacts how `research/projects/:id` is resolved depending on context
 * @param projectAccess - Project access response information for resolving project titles from ID
 * @param datasetId - Current dataset ID for resolving dataset paths
 * @param projectFilesDirectories - Projects whose files directories resolve titles from ID
 */
export const getFileTitle = (
  key: string,
  multiProject: boolean,
  projectAccess: SWRResponse<ProjectRolePrivilege[]>,
  datasetId?: string,
  projectFilesDirectories?: ProjectListItem[]
): { title?: string; loading?: boolean } => {
  const resolvedPath = resolveRouterPath(key, multiProject, datasetId);
  const isProjectsDir = resolvedPath.startsWith(BaseDirectory.PROJECTS);
  const splitPath = resolvedPath.split("/").filter(String);
  const title = splitPath.at(-1) ?? "Files";
  const overrides = BASE_DIRECTORIES[resolvedPath];

  if (overrides?.title) {
    // Use display overrides for base directory names
    return { title: overrides.title };
  }

  if (splitPath[0] === BaseDirectory.PROJECT_FILES_LIST && splitPath.length === 2) {
    if (!projectFilesDirectories) {
      return { loading: true };
    }
    return { title: projectFilesDirectories.find(({ id }) => id === title)?.name ?? title };
  }

  // Path matches as project directory and only has two segments so title must be project ID
  if (isProjectsDir && splitPath.length === 2) {
    if (projectAccess.isLoading) {
      return { loading: true };
    }
    const projectTitle = projectAccess?.data?.find(
      ({ project_id }) => String(project_id) === title
    )?.project_title;

    if (projectTitle) {
      return { title: projectTitle };
    }
  }

  return { title };
};

const useFileTitle = (key: string) => {
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const { datasetId } = useParams();
  const projectAccess = useProjectsAccessList();
  const projectFilesDirectories = useProjectFilesDirectories();
  return getFileTitle(key, isGlobalFileContext, projectAccess, datasetId, projectFilesDirectories);
};

export default useFileTitle;
